import React, { useState } from 'react';

const C = {
  bg:     '#0a0a0c',
  bg2:    '#13131a',
  bg3:    '#1a1a24',
  gold:   '#c9a84c',
  goldL:  '#e8c97a',
  sage:   '#7aab8a',
  rose:   '#c47a7a',
  text:   '#f0ede6',
  muted:  '#8a8590',
  dim:    '#4a4852',
  border: 'rgba(201,168,76,0.15)',
};

const serif    = { fontFamily: "'Cormorant Garamond', serif" };
const card     = { background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '22px' };
const btnGold  = { background: `linear-gradient(135deg,${C.gold},${C.goldL})`, color: '#0a0a0c', border: 'none', padding: '10px 24px', borderRadius: '6px', fontWeight: 600, fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: C.gold, border: `0.5px solid ${C.border}`, padding: '8px 18px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', letterSpacing: '0.04em', fontFamily: 'Jost, sans-serif' };
const tabBtn   = (active) => ({ padding: '9px 20px', borderRadius: '6px', border: `0.5px solid ${active ? C.gold : C.border}`, background: active ? 'rgba(201,168,76,0.1)' : 'transparent', color: active ? C.goldL : C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'Jost, sans-serif', letterSpacing: '0.04em' });
const optStyle = (state) => {
  if (state === 'correct') return { bg: 'rgba(122,171,138,0.12)', border: 'rgba(122,171,138,0.5)', color: C.sage };
  if (state === 'wrong')   return { bg: 'rgba(196,122,122,0.12)', border: 'rgba(196,122,122,0.4)', color: C.rose };
  if (state === 'dim')     return { bg: 'transparent', border: C.border, color: C.dim };
  if (state === 'hover')   return { bg: 'rgba(201,168,76,0.08)', border: 'rgba(201,168,76,0.4)', color: C.goldL };
  return { bg: C.bg2, border: C.border, color: C.text };
};

// ─── SVG CANDLESTICK ──────────────────────────────────────────────────────────
function CandleSVG({ candles, svgWidth, svgHeight = 130, priceMin, priceMax, bodyW = 26 }) {
  const toY = (p) => svgHeight * 0.94 - svgHeight * 0.88 * ((p - priceMin) / (priceMax - priceMin));
  return (
    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%"
      style={{ maxWidth: svgWidth * 2.4, display: 'block', margin: '0 auto' }}>
      {candles.map((c, i) => {
        const isBull = c.close >= c.open;
        const col = isBull ? C.sage : C.rose;
        const bTop = Math.min(toY(c.open), toY(c.close));
        const bBot = Math.max(toY(c.open), toY(c.close));
        const bH   = Math.max(bBot - bTop, 2.5);
        return (
          <g key={i}>
            <line x1={c.cx} y1={toY(c.high)} x2={c.cx} y2={toY(c.low)}
              stroke={col} strokeWidth="1.5" strokeLinecap="round"/>
            <rect x={c.cx - bodyW / 2} y={bTop} width={bodyW} height={bH}
              fill={col} rx="2" opacity={0.88}/>
          </g>
        );
      })}
    </svg>
  );
}

// ─── PATTERNS (MODE 1) ────────────────────────────────────────────────────────
const PATTERNS = [
  {
    id: 'doji', name: 'Doji',
    svgWidth: 80, priceMin: 30, priceMax: 90,
    candles: [{ cx: 40, open: 60, close: 60.4, high: 80, low: 40 }],
    options: ['Doji', 'Hammer', 'Spinning Top', 'Shooting Star'],
    correct: 0, signal: 'Neutral — indecision',
    meaning: 'Open and close are virtually identical — buyers and sellers fought to a complete standstill. The market is paralyzed with indecision.',
    lynnSays: "A Doji after a strong trend is a yellow light. I don't trade into it. I wait for the next candle to show me which side wins before committing.",
  },
  {
    id: 'hammer', name: 'Hammer',
    svgWidth: 80, priceMin: 20, priceMax: 90,
    candles: [{ cx: 40, open: 73, close: 77, high: 79, low: 24 }],
    options: ['Hammer', 'Shooting Star', 'Doji', 'Bearish Engulfing'],
    correct: 0, signal: 'Bullish reversal (at support)',
    meaning: 'Small body near the top, long lower wick. Sellers drove price way down but buyers roared back and closed near the high. Bullish rejection at support.',
    lynnSays: "A Hammer at a key support level is one of my favorite setups for a CSP. Buyers just showed up in force — they defended that price.",
  },
  {
    id: 'shooting_star', name: 'Shooting Star',
    svgWidth: 80, priceMin: 30, priceMax: 100,
    candles: [{ cx: 40, open: 52, close: 47, high: 90, low: 43 }],
    options: ['Shooting Star', 'Hammer', 'Spinning Top', 'Doji'],
    correct: 0, signal: 'Bearish reversal (at resistance)',
    meaning: 'Small body near the bottom, long upper wick. Buyers pushed price way up but sellers slammed it back down. Bearish rejection at resistance.',
    lynnSays: "Shooting Stars at resistance tell me a rally is failing. I start thinking about covered calls or taking profits on long positions.",
  },
  {
    id: 'spinning_top', name: 'Spinning Top',
    svgWidth: 80, priceMin: 30, priceMax: 90,
    candles: [{ cx: 40, open: 56, close: 64, high: 79, low: 41 }],
    options: ['Spinning Top', 'Doji', 'Bullish Engulfing', 'Hammer'],
    correct: 0, signal: 'Neutral — indecision',
    meaning: 'Small real body with wicks on both sides. Like a Doji but with a slightly larger body — still signals indecision and loss of directional momentum.',
    lynnSays: "Doji, Spinning Top — both mean 'wait.' The market is catching its breath. Don't jump in front of a decision it hasn't made yet.",
  },
  {
    id: 'bullish_engulfing', name: 'Bullish Engulfing',
    svgWidth: 140, priceMin: 30, priceMax: 90,
    candles: [
      { cx: 40,  open: 71, close: 58, high: 73, low: 56 },
      { cx: 100, open: 55, close: 74, high: 76, low: 53 },
    ],
    options: ['Bullish Engulfing', 'Bearish Engulfing', 'Morning Star', 'Bullish Harami'],
    correct: 0, signal: 'Bullish reversal',
    meaning: "A bearish candle followed by a larger bullish candle that completely engulfs it. Buyers overpowered sellers decisively — strong reversal signal.",
    lynnSays: "Bullish Engulfing at a support level is one of the highest-confidence reversal patterns I trade. Buyers didn't just show up — they dominated.",
  },
  {
    id: 'bearish_engulfing', name: 'Bearish Engulfing',
    svgWidth: 140, priceMin: 30, priceMax: 90,
    candles: [
      { cx: 40,  open: 54, close: 67, high: 69, low: 52 },
      { cx: 100, open: 70, close: 54, high: 72, low: 52 },
    ],
    options: ['Bearish Engulfing', 'Bullish Engulfing', 'Evening Star', 'Bearish Harami'],
    correct: 0, signal: 'Bearish reversal',
    meaning: "A bullish candle followed by a larger bearish candle that engulfs it. Sellers seized control decisively. Bearish reversal at resistance.",
    lynnSays: "Bearish Engulfing at resistance tells me to protect profits and be cautious about new bullish entries. Sellers are in charge now.",
  },
  {
    id: 'morning_star', name: 'Morning Star',
    svgWidth: 200, priceMin: 20, priceMax: 90,
    candles: [
      { cx: 35,  open: 78, close: 58, high: 80, low: 56 },
      { cx: 100, open: 52, close: 54, high: 58, low: 48 },
      { cx: 165, open: 57, close: 74, high: 76, low: 55 },
    ],
    options: ['Morning Star', 'Evening Star', 'Three Green Soldiers', 'Bullish Engulfing'],
    correct: 0, signal: 'Bullish reversal',
    meaning: "Three-candle bottom reversal: large bearish candle, small 'star' of indecision, then a large bullish recovery. One of the most reliable bottom patterns.",
    lynnSays: "Three candles, three days, a complete shift in control. Morning Star at a key support level is a setup I get genuinely excited about.",
  },
  {
    id: 'evening_star', name: 'Evening Star',
    svgWidth: 200, priceMin: 25, priceMax: 95,
    candles: [
      { cx: 35,  open: 48, close: 68, high: 70, low: 46 },
      { cx: 100, open: 72, close: 74, high: 79, low: 69 },
      { cx: 165, open: 71, close: 52, high: 73, low: 50 },
    ],
    options: ['Evening Star', 'Morning Star', 'Three Red Crows', 'Bearish Engulfing'],
    correct: 0, signal: 'Bearish reversal',
    meaning: "Three-candle top reversal: large bullish, small star at the highs (indecision), large bearish reversal. The bearish counterpart to Morning Star.",
    lynnSays: "Evening Star at a resistance level tells me the rally is exhausted. I start looking for exits on long positions and avoid new bullish setups.",
  },
];

// ─── PREDICT SCENARIOS (MODE 2) ───────────────────────────────────────────────
const PREDICT_SCENES = [
  {
    id: 1, title: 'The Uptrend Exhaustion',
    svgWidth: 390, priceMin: 30, priceMax: 95,
    candles: [
      { cx: 35,  open: 40, close: 50, high: 52, low: 38 },
      { cx: 100, open: 49, close: 59, high: 61, low: 47 },
      { cx: 165, open: 58, close: 68, high: 70, low: 56 },
      { cx: 230, open: 67, close: 77, high: 79, low: 65 },
      { cx: 295, open: 76, close: 85, high: 87, low: 74 },
      { cx: 360, open: 83, close: 80, high: 91, low: 78 },
    ],
    question: 'Five consecutive bullish candles, then a small bearish candle with a long upper wick at the highs. What is most likely next?',
    options: [
      'Continues strongly higher — the trend is powerful',
      'Pause or reversal — the upper wick shows sellers appeared at the highs',
      'Immediate crash — five bullish candles always reverse',
      'Nothing — ignore single-candle signals',
    ],
    correct: 1,
    explain: "After five bullish candles, a bearish candle with a long upper wick signals sellers are showing up at the highs. Momentum is fading. The trend may continue, but risk is rising — smart traders tighten stops and watch the next candle for confirmation.",
  },
  {
    id: 2, title: 'The Support Bounce',
    svgWidth: 390, priceMin: 25, priceMax: 88,
    candles: [
      { cx: 35,  open: 76, close: 66, high: 78, low: 64 },
      { cx: 100, open: 65, close: 58, high: 67, low: 56 },
      { cx: 165, open: 57, close: 49, high: 59, low: 36 },
      { cx: 230, open: 48, close: 56, high: 58, low: 46 },
      { cx: 295, open: 55, close: 63, high: 65, low: 53 },
      { cx: 360, open: 62, close: 70, high: 72, low: 60 },
    ],
    question: 'A downtrend, then a candle with a dramatic long lower wick. Two strong bullish candles follow. What is most likely next?',
    options: [
      'More selling — the downtrend continues regardless',
      'Continued recovery — support held and buyers are taking control',
      'Immediate breakout to all-time highs',
      'A gap down overnight — the bounce is always a trap',
    ],
    correct: 1,
    explain: "The long lower wick shows buyers stepped in aggressively and rejected lower prices. The follow-through bullish candles confirm the reversal is real. This is exactly the pattern Lynn looks for before selling a Cash-Secured Put — support held, and buyers are now in control.",
  },
  {
    id: 3, title: 'Volatility Compression',
    svgWidth: 390, priceMin: 44, priceMax: 82,
    candles: [
      { cx: 35,  open: 58, close: 66, high: 70, low: 56 },
      { cx: 100, open: 65, close: 61, high: 68, low: 59 },
      { cx: 165, open: 62, close: 64, high: 66, low: 60 },
      { cx: 230, open: 63, close: 62, high: 65, low: 61 },
      { cx: 295, open: 62.5, close: 63, high: 64, low: 61.5 },
      { cx: 360, open: 63, close: 63.1, high: 63.7, low: 62.4 },
    ],
    question: "Each candle's range is getting smaller and smaller, ending in a near-Doji. What is this pattern, and what most likely comes next?",
    options: [
      'The stock is dying — sell everything immediately',
      'Compression. A big move is coiling — wait for the breakout to define direction.',
      'Definitively breaks higher — compression is always bullish',
      'Random noise — no actionable information here',
    ],
    correct: 1,
    explain: "This is compression — volatility contracting into a tightening range, like a coiled spring. Neither buyers nor sellers are winning. The next big candle often determines direction. IV also drops during compression, which can make options cheap right before a major move. Lynn watches these setups carefully.",
  },
  {
    id: 4, title: 'The Bull Trap',
    svgWidth: 390, priceMin: 32, priceMax: 96,
    candles: [
      { cx: 35,  open: 55, close: 63, high: 65, low: 53 },
      { cx: 100, open: 62, close: 70, high: 72, low: 60 },
      { cx: 165, open: 69, close: 80, high: 85, low: 67 },
      { cx: 230, open: 82, close: 67, high: 84, low: 65 },
      { cx: 295, open: 66, close: 59, high: 68, low: 57 },
      { cx: 360, open: 58, close: 52, high: 60, low: 50 },
    ],
    question: "Price breaks to new highs on a strong candle, then immediately reverses hard with a large bearish candle. Two more bearish candles follow. What happened?",
    options: [
      'Buy the dip — this is a great discount entry',
      'Failed breakout / bull trap. Sellers aggressively rejected the new highs.',
      'Normal consolidation before more upside',
      'Unpredictable — no way to read this pattern',
    ],
    correct: 1,
    explain: "This is a classic bull trap — also called a failed breakout. Price broke resistance, luring in buyers, then sellers overwhelmed them and pushed price back below resistance. The large reversal candle closing below the breakout level is the key signal. This is a bearish setup — not a buying opportunity.",
  },
];

// ─── BUILD PATTERN MEANINGS (MODE 3) ─────────────────────────────────────────
const BUILD_MEANINGS = {
  'bullish-none-short-short':   { name: 'Doji (Bullish context)',    meaning: 'Nearly identical open and close. Indecision — the market is undecided.' },
  'bearish-none-short-short':   { name: 'Doji (Bearish context)',    meaning: 'Nearly identical open and close. Indecision — wait for direction before trading.' },
  'bullish-none-long-long':     { name: 'Long-Legged Doji',          meaning: 'Price swung wildly but closed where it opened. Extreme uncertainty in both directions.' },
  'bearish-none-long-long':     { name: 'Long-Legged Doji',          meaning: 'Price swung wildly but closed where it opened. Extreme uncertainty in both directions.' },
  'bullish-small-none-none':    { name: 'Small Bullish Candle',      meaning: 'Modest buying pressure. Bulls in control but not with conviction — common in consolidation.' },
  'bullish-small-none-long':    { name: 'Hammer',                    meaning: 'Buyers rejected lower prices decisively. Bullish reversal signal, especially at support.' },
  'bullish-small-long-none':    { name: 'Inverted Hammer',           meaning: 'Buyers tried to push higher but were rejected. Watch for follow-through before acting bullishly.' },
  'bullish-small-short-short':  { name: 'Spinning Top (Bullish)',    meaning: 'Small body with wicks on both sides — indecision. Neither buyers nor sellers dominating.' },
  'bearish-small-none-none':    { name: 'Small Bearish Candle',      meaning: 'Modest selling. Bears in control but not with conviction — often part of consolidation.' },
  'bearish-small-long-none':    { name: 'Shooting Star',             meaning: 'Buyers pushed high but sellers slammed price back down. Bearish rejection at resistance.' },
  'bearish-small-none-long':    { name: 'Hanging Man',               meaning: 'Warning sign at the top of an uptrend. Sellers showed up — wait for bearish confirmation.' },
  'bearish-small-short-short':  { name: 'Spinning Top (Bearish)',    meaning: 'Small bearish body with both wicks — indecision. Neither side winning decisively.' },
  'bullish-medium-none-none':   { name: 'Bullish Candle',            meaning: 'Solid buying session. Price closed significantly above the open — healthy bullish momentum.' },
  'bullish-medium-short-short': { name: 'Bullish Real Body',         meaning: 'Healthy bullish candle with minor wicks. Clean buying with some two-way price action.' },
  'bullish-medium-none-long':   { name: 'Bullish Hammer',            meaning: 'Medium bullish body with long lower wick. Strong buying from the lows — very bullish at support.' },
  'bullish-medium-long-none':   { name: 'Bullish Shooting Star body',meaning: 'Buyers won the session but were rejected from higher prices. Cautious bullishness.' },
  'bearish-medium-none-none':   { name: 'Bearish Candle',            meaning: 'Solid selling session. Sellers controlled the day from open to close.' },
  'bearish-medium-short-short': { name: 'Bearish Real Body',         meaning: 'Clear selling with minor wicks. Clean bearish momentum — sellers in charge.' },
  'bearish-medium-long-none':   { name: 'Bearish Shooting Star',     meaning: 'Buyers tried hard but sellers ultimately dominated. Bearish rejection of higher prices.' },
  'bullish-large-none-none':    { name: 'Bullish Marubozu',          meaning: 'Opened at the low, closed at the high. Pure, relentless buying pressure — extremely bullish.' },
  'bullish-large-short-short':  { name: 'Strong Bullish Candle',     meaning: 'Dominant buying. Small wicks show sellers tried briefly but buyers overwhelmed them decisively.' },
  'bearish-large-none-none':    { name: 'Bearish Marubozu',          meaning: 'Opened at the high, closed at the low. Pure, relentless selling pressure — extremely bearish.' },
  'bearish-large-short-short':  { name: 'Strong Bearish Candle',     meaning: 'Dominant selling. Small wicks show buyers tried but sellers overwhelmed them completely.' },
};

// ─── MODE 1: IDENTIFY ─────────────────────────────────────────────────────────
function IdentifyMode({ onXpAward, onAllComplete }) {
  const [idx, setIdx]       = useState(0);
  const [picked, setPicked] = useState(null);
  const [xpEarned, setXpEarned] = useState(0);

  const pattern  = PATTERNS[idx];
  const answered = picked !== null;
  const correct  = picked === pattern.correct;

  const handlePick = (i) => {
    if (answered) return;
    setPicked(i);
    const xp = i === pattern.correct ? 30 : 10;
    setXpEarned(x => x + xp);
    onXpAward && onXpAward(xp);
    if (idx === PATTERNS.length - 1) onAllComplete && onAllComplete();
  };

  const next = () => { setPicked(null); setIdx(i => (i + 1) % PATTERNS.length); };

  const s = (oi) => {
    if (!answered) return optStyle('default');
    if (oi === pattern.correct) return optStyle('correct');
    if (oi === picked) return optStyle('wrong');
    return optStyle('dim');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: C.muted }}>Pattern {idx + 1} of {PATTERNS.length}</span>
        <span style={{ fontSize: '11px', color: C.gold }}>+{xpEarned} XP earned this session</span>
      </div>
      <div style={{ height: '3px', background: C.bg3, borderRadius: '2px', marginBottom: '24px' }}>
        <div style={{ height: '100%', width: `${(idx / PATTERNS.length) * 100}%`, background: `linear-gradient(90deg,${C.gold},${C.goldL})`, borderRadius: '2px', transition: 'width .4s ease' }}/>
      </div>

      <div style={{ ...card, marginBottom: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', color: C.muted, marginBottom: '16px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          What candlestick pattern is this?
        </p>
        <div style={{ padding: '10px 0 6px' }}>
          <CandleSVG candles={pattern.candles} svgWidth={pattern.svgWidth} priceMin={pattern.priceMin} priceMax={pattern.priceMax}/>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {pattern.options.map((opt, i) => {
          const st = s(i);
          return (
            <div key={i} onClick={() => handlePick(i)}
              style={{ padding: '12px 16px', borderRadius: '8px', border: `0.5px solid ${st.border}`, background: st.bg, color: st.color, fontSize: '13px', cursor: answered ? 'default' : 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: '10px', lineHeight: 1.45 }}>
              {answered && i === pattern.correct && <span style={{ color: C.sage, flexShrink: 0 }}>✓</span>}
              {answered && i === picked && i !== pattern.correct && <span style={{ color: C.rose, flexShrink: 0 }}>✗</span>}
              {opt}
            </div>
          );
        })}
      </div>

      {answered && (
        <div style={{ ...card, borderColor: correct ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.25)', marginBottom: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {pattern.name} · {pattern.signal}
            </span>
            <span style={{ fontSize: '11px', color: correct ? C.sage : C.rose }}>
              {correct ? '+30 XP ✓' : '+10 XP'}
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.78, marginBottom: '12px' }}>
            {pattern.meaning}
          </p>
          <div style={{ background: C.bg3, borderRadius: '6px', padding: '12px', borderLeft: `2px solid ${C.gold}`, marginBottom: '16px' }}>
            <span style={{ fontSize: '11px', color: C.gold }}>Lynn says: </span>
            <span style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7 }}>{pattern.lynnSays}</span>
          </div>
          <button style={{ ...btnGold, width: '100%' }} onClick={next}>
            {idx < PATTERNS.length - 1 ? 'Next Pattern →' : 'Start Over ↺'}
          </button>
        </div>
      )}

      {!answered && (
        <p style={{ fontSize: '12px', color: C.dim, textAlign: 'center', marginTop: '8px' }}>
          Select an answer above.
        </p>
      )}
    </div>
  );
}

// ─── MODE 2: PREDICT ──────────────────────────────────────────────────────────
function PredictMode({ onXpAward }) {
  const [idx, setIdx]       = useState(0);
  const [picked, setPicked] = useState(null);
  const [xpEarned, setXpEarned] = useState(0);

  const scene    = PREDICT_SCENES[idx];
  const answered = picked !== null;
  const correct  = picked === scene.correct;

  const handlePick = (i) => {
    if (answered) return;
    setPicked(i);
    const xp = i === scene.correct ? 40 : 15;
    setXpEarned(x => x + xp);
    onXpAward && onXpAward(xp);
  };

  const next = () => { setPicked(null); setIdx(i => (i + 1) % PREDICT_SCENES.length); };

  const s = (oi) => {
    if (!answered) return optStyle('default');
    if (oi === scene.correct) return optStyle('correct');
    if (oi === picked) return optStyle('wrong');
    return optStyle('dim');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: C.muted }}>Scenario {idx + 1} of {PREDICT_SCENES.length}</span>
        <span style={{ fontSize: '11px', color: C.gold }}>+{xpEarned} XP earned this session</span>
      </div>
      <div style={{ height: '3px', background: C.bg3, borderRadius: '2px', marginBottom: '24px' }}>
        <div style={{ height: '100%', width: `${(idx / PREDICT_SCENES.length) * 100}%`, background: `linear-gradient(90deg,${C.gold},${C.goldL})`, borderRadius: '2px', transition: 'width .4s ease' }}/>
      </div>

      <div style={{ ...card, marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
          {scene.title}
        </div>
        <CandleSVG candles={scene.candles} svgWidth={scene.svgWidth} priceMin={scene.priceMin} priceMax={scene.priceMax} bodyW={22}/>
        <p style={{ fontSize: '13px', color: C.text, lineHeight: 1.75, marginTop: '16px', fontStyle: 'italic' }}>
          {scene.question}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {scene.options.map((opt, i) => {
          const st = s(i);
          return (
            <div key={i} onClick={() => handlePick(i)}
              style={{ padding: '12px 16px', borderRadius: '8px', border: `0.5px solid ${st.border}`, background: st.bg, color: st.color, fontSize: '13px', cursor: answered ? 'default' : 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: '10px', lineHeight: 1.45 }}>
              {answered && i === scene.correct && <span style={{ color: C.sage, flexShrink: 0 }}>✓</span>}
              {answered && i === picked && i !== scene.correct && <span style={{ color: C.rose, flexShrink: 0 }}>✗</span>}
              {opt}
            </div>
          );
        })}
      </div>

      {answered && (
        <div style={{ ...card, borderColor: correct ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.25)' }}>
          <div style={{ fontSize: '12px', color: correct ? C.sage : C.rose, marginBottom: '10px' }}>
            {correct ? `✓ Correct — +40 XP` : `✗ Not quite — +15 XP`}
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.78, marginBottom: '16px' }}>
            {scene.explain}
          </p>
          <button style={{ ...btnGold, width: '100%' }} onClick={next}>
            {idx < PREDICT_SCENES.length - 1 ? 'Next Scenario →' : 'Start Over ↺'}
          </button>
        </div>
      )}

      {!answered && (
        <p style={{ fontSize: '12px', color: C.dim, textAlign: 'center', marginTop: '8px' }}>
          Select an answer above.
        </p>
      )}
    </div>
  );
}

// ─── MODE 3: BUILD ────────────────────────────────────────────────────────────
function BuildMode() {
  const [direction, setDirection] = useState('bullish');
  const [bodySize,  setBodySize]  = useState('medium');
  const [upperWick, setUpperWick] = useState('short');
  const [lowerWick, setLowerWick] = useState('short');

  const key  = `${direction}-${bodySize}-${upperWick}-${lowerWick}`;
  const info = BUILD_MEANINGS[key] || {
    name: 'Custom Pattern',
    meaning: 'This combination creates a unique candlestick. Study the body-to-wick ratio — it tells you the balance of power between buyers and sellers during that session.',
  };

  const isBull = direction === 'bullish';
  const color  = isBull ? C.sage : C.rose;

  const svgW = 140, svgH = 160, cx = 70;
  const midY = svgH / 2;
  const bodyHMap = { none: 2, small: 18, medium: 38, large: 62 };
  const wickMap  = { none: 0, short: 22, long: 48 };
  const bH   = bodyHMap[bodySize];
  const uW   = wickMap[upperWick];
  const lW   = wickMap[lowerWick];
  const bTop = midY - bH / 2;
  const bBot = midY + bH / 2;
  const wTop = bTop - uW;
  const wBot = bBot + lW;

  const controlGroup = (label, value, setter, options, colorFn) => (
    <div style={{ flex: 1, minWidth: '150px' }}>
      <div style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</div>
      {options.map(([val, display]) => {
        const active = value === val;
        const accent = colorFn ? colorFn(val) : C.gold;
        return (
          <button key={val} onClick={() => setter(val)}
            style={{ display: 'block', width: '100%', padding: '8px 14px', marginBottom: '6px', borderRadius: '6px', border: `0.5px solid ${active ? accent : C.border}`, background: active ? `${accent}20` : 'transparent', color: active ? accent : C.muted, fontSize: '13px', cursor: 'pointer', textAlign: 'left', fontFamily: 'Jost, sans-serif', transition: 'all .15s' }}>
            {display}
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '22px' }}>
        Build a candlestick using the controls below and see what pattern you've created.
      </p>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {controlGroup('Direction', direction, setDirection,
          [['bullish', '▲ Bullish (green)'], ['bearish', '▼ Bearish (red)']],
          (v) => v === 'bullish' ? C.sage : C.rose
        )}
        {controlGroup('Body Size', bodySize, setBodySize,
          [['none', 'None (Doji)'], ['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']],
          null
        )}
        {controlGroup('Upper Wick', upperWick, setUpperWick,
          [['none', 'None'], ['short', 'Short'], ['long', 'Long']],
          null
        )}
        {controlGroup('Lower Wick', lowerWick, setLowerWick,
          [['none', 'None'], ['short', 'Short'], ['long', 'Long']],
          null
        )}
      </div>

      <div style={{ ...card, textAlign: 'center', marginBottom: '16px' }}>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} width="140" height="160"
          style={{ display: 'block', margin: '0 auto 16px' }}>
          {(uW > 0 || lW > 0) && (
            <line x1={cx} y1={wTop} x2={cx} y2={wBot}
              stroke={color} strokeWidth="2" strokeLinecap="round"/>
          )}
          <rect x={cx - 19} y={bTop} width={38} height={Math.max(bH, 2.5)}
            fill={color} rx="2.5" opacity={0.9}/>
        </svg>
        <div style={{ ...serif, fontSize: '22px', color: C.goldL, marginBottom: '6px' }}>{info.name}</div>
        <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.8)', lineHeight: 1.78 }}>{info.meaning}</p>
      </div>

      <div style={{ background: C.bg3, borderRadius: '8px', padding: '14px 16px', borderLeft: `2px solid ${C.gold}` }}>
        <p style={{ fontSize: '12px', color: C.muted, lineHeight: 1.78 }}>
          <span style={{ color: C.goldL }}>Reading the candle: </span>
          The <span style={{ color }}>{direction}</span> body shows price {isBull ? 'closed above' : 'closed below'} the open — {isBull ? 'buyers won the session' : 'sellers won the session'}.
          {upperWick !== 'none' && ` The ${upperWick} upper wick means sellers ${upperWick === 'long' ? 'aggressively pushed back from the highs' : 'briefly rejected higher prices'}.`}
          {lowerWick !== 'none' && ` The ${lowerWick} lower wick means buyers ${lowerWick === 'long' ? 'strongly defended the lows and staged a major comeback' : 'stepped in to support prices from going lower'}.`}
          {upperWick === 'none' && lowerWick === 'none' && ' No wicks mean price moved cleanly from open to close — no two-way action at all.'}
        </p>
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function CandlestickSimulator({ onXpAward, onBack, onNavigate, onAllComplete }) {
  const [mode, setMode] = useState('identify');

  return (
    <div style={{ padding: '28px 24px', maxWidth: '820px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '10px' }}>
        {onNavigate && <button style={btnGhost} onClick={() => onNavigate('chapter_ch02')}>← Chapter 2</button>}
        <button style={btnGhost} onClick={onBack}>← Dashboard</button>
      </div>

      <div style={{ marginTop: '20px', marginBottom: '6px', fontSize: '10px', color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        Visual Trading Skills
      </div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: C.text, margin: '0 0 4px' }}>
        Candlestick Simulator
      </h2>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '16px' }}>
        Three modes: identify patterns, predict what comes next, and build your own candles.
      </p>

      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
        <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
          Three Ways to Practice
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {[
            ['◉', 'Identify', 'Name the pattern you see'],
            ['◈', 'Predict',  'What candle comes next?'],
            ['◧', 'Build',    'Build a candle from scratch'],
          ].map(([icon, name, desc]) => (
            <div key={name} style={{ flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '13px', color: C.goldL, marginBottom: '3px' }}>{icon} {name}</div>
              <div style={{ fontSize: '12px', color: C.muted }}>{desc}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '11px', color: C.muted, fontStyle: 'italic', margin: 0 }}>
          Try all three — each one builds a different skill.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {[
          ['identify', '◉', 'IDENTIFY', 'Name the pattern'],
          ['predict',  '◈', 'PREDICT',  'What comes next?'],
          ['build',    '◧', 'BUILD',    'Build your own'],
        ].map(([m, icon, label, desc]) => (
          <button key={m} onClick={() => setMode(m)} style={{
            flex: 1,
            minWidth: '120px',
            padding: '14px 12px',
            borderRadius: '8px',
            border: `0.5px solid ${mode === m ? C.gold : C.border}`,
            background: mode === m ? C.gold : 'transparent',
            color: mode === m ? '#0a0a0c' : C.muted,
            cursor: 'pointer',
            textAlign: 'center',
            fontFamily: 'Jost, sans-serif',
            transition: 'all .2s',
          }}>
            <div style={{ fontSize: '18px', marginBottom: '4px' }}>{icon}</div>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '3px' }}>{label}</div>
            <div style={{ fontSize: '11px', opacity: mode === m ? 0.75 : 0.6 }}>{desc}</div>
          </button>
        ))}
      </div>

      {mode === 'identify' && <IdentifyMode onXpAward={onXpAward} onAllComplete={onAllComplete} />}
      {mode === 'predict'  && <PredictMode  onXpAward={onXpAward} />}
      {mode === 'build'    && <BuildMode />}
    </div>
  );
}
